import supabase from "@/lib/supabase";
import { UserQuestProgressService } from "@/lib/userQuestProgressService";
import useAuthStore from "@/store/useAuthStore";
import { ModeType } from "./schema";
import { Quest } from "./schema";
import { addDiamond } from "./reward";
import { getQuestModes, getAllQuests } from "@/lib/questHelper";
import { getProgressFromDB, getScheduleDailyTasks, getSchedules, getFinishedLessonFromDB } from "./schedule";
import { fetchPendingMistakes } from "./mistake";
import { getLocalDateString } from "@/lib/utils";

export interface Task {
  name: string;
  description: string;
  unique_key: string;
  rewardDiamond: number;
  details?: string;
  type?: string;
  count?: number;
  quest_id?: string;
  id?: string;
  progress?: any;
  status?: string;
  finished_at?: string;
}

const BASE_TASKS: {
  easy: Task[];
  medium: Task[];
  hard: Task[];
} = {
  easy: [
    {
      name: "Daily Learn",
      description: `Finish 1 Learn`,
      details: `Finish 1 Learn session`,
      rewardDiamond: 10,
      type: 'normal',
      unique_key: "easy",
    },
    {
      name: "Daily Practice",
      description: `Finish 1 Practice`,
      details: "Finish 1 Practice session",
      rewardDiamond: 10,
      type: 'normal',
      unique_key: "easy",
    },
    {
      name: "Different Subject Learn",
      description: `Finish 2 Learns in different subjects`,
      details: "Finish 2 Learns in different subjects (e.g., complete 1 Math Learn + 1 Science Learn)",
      rewardDiamond: 15,
      count: 2,
      type: 'normal',
      unique_key: "easy",
    },
    {
      name: "Different Subject Practice",
      description: `Finish 2 Practices in different subjects`,
      details: "Finish 2 Practices in different subjects (e.g., complete 1 Math Practice + 1 Vocabulary Practice)",
      count: 2,
      rewardDiamond: 15,
      type: 'normal',
      unique_key: "easy",
    },
  ],
  medium: [
    {
      name: "Daily Lesson",
      description: "Complete 1 lesson",
      details: "Complete 1 lesson (both Learn and Practice)",
      rewardDiamond: 20,
      type: 'normal',
      unique_key: "medium",
    },
    {
      name: "Great Practices",
      description: "Answer 80% correctly in 2 practices",
      details: "Answer 80% correctly in 2 practices (e.g., 8/10 correct in one practice, 9/10 correct in another practice)",
      count: 2,
      rewardDiamond: 20,
      type: 'normal',
      unique_key: "medium",
    },
  ],
  hard: [
    {
      name: "Daily Chapter",
      description: "Complete 1 chapter",
      details: "Complete 1 chapter (e.g., complete all Learn and Practice in a Chapter)",
      rewardDiamond: 50,
      type: 'normal',
      unique_key: "hard",
    },
    {
      name: "Different Subject Lessons",
      description: "Complete 2 lessons in different subjects",
      details: "Complete 2 lessons in different subjects (e.g., complete 1 Math lesson with learn and practice + 1 Science lesson with learn and practice)",
      rewardDiamond: 40,
      count: 2,
      type: 'normal',
      unique_key: "hard",
    },
  ],
}

const getScheduleTaskByName = (name: string, count: number, quest_id: string): Task => {
  return {
    name: name,
    description: "Complete " + count + (count > 1 ? " lessons" : " lesson") + " in " + name.split('-')[1],
    rewardDiamond: 10 * count,
    details: "Complete " + count + (count > 1 ? " lessons" : " lesson") + " in " + name.split('-')[1],
    unique_key: quest_id,
  }
}

const getMistakesTaskByName = (name: string, count: number): Task => {
  return {
    name: name,
    description: "Finish mistakes practice in " + name.split('-')[1],
    rewardDiamond: 15,
    type: 'mistakes',
    unique_key: "mistakes",
  }
}

const getTaskByName = (name: string, count: number, quest_id: string): Task => {
  if (name.startsWith('schedule-')) {
    return getScheduleTaskByName(name, count, quest_id);
  } else if (name.startsWith('mistakes-')) {
    return getMistakesTaskByName(name, count);
  }
  const task = BASE_TASKS.easy.find(task => task.name === name) ||
    BASE_TASKS.medium.find(task => task.name === name) ||
    BASE_TASKS.hard.find(task => task.name === name);
  if (!task) {
    throw new Error(`Task ${name} not found`);
  }
  return task;
}

const updateTaskStatusDb = async (task: Task): Promise<void> => {
  if (!task.id) {
    throw new Error('Task id is required');
  }

  const { error, data } = await supabase.from('task_status').update({
    finished_at: new Date().toISOString(),
    status: task.status,
    progress: task.progress,
  })
    .eq('id', task.id)
    .select();

  console.log('Update result:', { error, data, affectedRows: data?.length });

  if (error) {
    console.error('Error updating task status:', error);
    throw error;
  }

};

/**
 * 该task中保存finished label，count是finished label的个数
 * @param task 
 * @param label 
 * @param count 
 * @returns 
 */
const updateProgress = (task: Task, label: string, count: number): Task | null => {
  if (task.progress?.finished?.length > 0) {
    if (task.progress.finished.includes(label)) {
      return null;
    } else {
      task.progress.finished.push(label);
    }
  } else {
    task.progress = {
      finished: [label],
    };
  }
  if (task.progress.finished.length >= count) {
    task.status = 'finished';
  } else {
    task.status = 'in_progress';
  }
  return task;
}

const isChapterAllFinished = async (userId: string, quest: Quest, chapterId: string, modeTypes: ModeType[]): Promise<boolean> => {
  const chapter = quest.chapters.find(chapter =>
    chapter.id === chapterId);
  if (chapter) {
    const chapterLessonIds = chapter.lesson_list.map(lesson => lesson.id);
    if (await UserQuestProgressService.isChapterAllFinished(userId, chapter.id, chapterLessonIds, modeTypes)) {
      return true;
    }
  }
  return false;
}

/**
 * 完成lesson后，更新数据库finished_lesson表，先更新mode，如果mode都完成，则更新lesson，如果lesson都完成，则更新chapter
 * @param userId 
 * @param quest 
 * @param chapterId 
 * @param lessonId 
 * @param mode 
 */
const finishLessonDb = async (userId: string, quest: Quest, chapterId: string, lessonId: string, mode: ModeType): Promise<void> => {
  const { data, error } = await supabase.from('finished_lesson').insert({
    user_id: userId,
    quest_id: quest.id,
    chapter_id: chapterId,
    lesson_id: lessonId,
    mode_type: mode,
  });
  if (error) {
    console.error('Error finishing lesson:', error);
    throw error;
  }
  const { lessonModes, chapterModes } = getQuestModes(quest);
  const chapterFinished = await isChapterAllFinished(userId, quest, chapterId, lessonModes);
  if (await UserQuestProgressService.isLessonAllModeFinished(userId, lessonId, lessonModes)) {
    const { data: lessonData, error: lessonError } = await supabase.from('finished_lesson').insert({
      user_id: userId,
      quest_id: quest.id,
      chapter_id: chapterId,
      lesson_id: lessonId,
      mode_type: "empty",
    });
    if (lessonError) {
      console.error('Error finishing lesson:', lessonError);
      throw lessonError;
    }
    if (await isChapterAllFinished(userId, quest, chapterId, lessonModes)) {
      const { data: chapterData, error: chapterError } = await supabase.from('finished_lesson').insert({
        user_id: userId,
        quest_id: quest.id,
        chapter_id: chapterId,
        lesson_id: "empty",
        mode_type: "empty",
      });
      if (chapterError) {
        console.error('Error finishing chapter:', chapterError);
        throw chapterError;
      }
    }
  }
}

export const finishMistakesPractice = async (subject: string): Promise<Task[]> => {
  const finishedTasks: Task[] = [];
  const user = useAuthStore.getState().user;
  if (!user) {
    return [];
  }
  const dailyTasks = await getAllTasksStatus();
  await Promise.all(dailyTasks.map(async (task) => {
    if (task.status === 'finished') {
      return;
    }

    if (task.name.startsWith('mistakes-')) {
      if (task.name.split('-')[1] === subject) {
        task.status = 'finished';
        await updateTaskStatusDb(task);
        finishedTasks.push(task);
      }
    }
  }));
  finishedTasks.forEach(task => {
    addDiamond(task.rewardDiamond);
  });
  return finishedTasks;
}

export const finishLesson = async (quest: Quest, chapterId: string, lessonId: string, mode: ModeType, correctRate?: number): Promise<Task[]> => {
  const finishedTasks: Task[] = [];
  const user = useAuthStore.getState().user;
  if (!user || !quest || !quest.subject) {
    return [];
  }
  await finishLessonDb(user.id, quest, chapterId, lessonId, mode);
  const { lessonModes, chapterModes } = getQuestModes(quest);
  const dailyTasks = await getAllTasksStatus();

  await Promise.all(dailyTasks.map(async (task) => {
    if (task.status === 'finished') {
      return;
    }

    if (task.name.startsWith('schedule-')) {
      if (task.quest_id !== quest.id) {
        return;
      }
      if (await UserQuestProgressService.isLessonAllModeFinished(user.id, lessonId, lessonModes)) {
        let new_task = updateProgress(task, lessonId === 'empty' ? chapterId : lessonId, task.count || 1);
        if (new_task) {
          await updateTaskStatusDb(new_task);
          if (new_task.status === 'finished') {
            finishedTasks.push(new_task);
          }
        }
      }
    } else if (task.name.startsWith('mistakes-')) {
      return;
    } else {
      switch (task.name) {
        case 'Daily Practice':
          if (mode === ModeType.PRACTICE || mode === ModeType.CHALLENGE) {
            task.status = 'finished';
            await updateTaskStatusDb(task);
            finishedTasks.push(task);
          }
          break;
        case 'Daily Learn':
          if (mode === ModeType.LEARN) {
            task.status = 'finished';
            await updateTaskStatusDb(task);
            finishedTasks.push(task);
          }
          break;
        case 'Different Subject Learn':
          if (mode === ModeType.LEARN) {
            let new_task = updateProgress(task, quest.subject!, task.count || 2);
            if (new_task) {
              await updateTaskStatusDb(new_task);
              if (new_task.status === 'finished') {
                finishedTasks.push(new_task);
              }
            }
          }
          break;
        case 'Different Subject Practice':
          if (mode === ModeType.PRACTICE || mode === ModeType.CHALLENGE) {
            let new_task = updateProgress(task, quest.subject!, task.count || 2);
            if (new_task) {
              await updateTaskStatusDb(new_task);
              if (new_task.status === 'finished') {
                finishedTasks.push(new_task);
              }
            }
          }
          break;
        case 'Daily Lesson':
          if (await UserQuestProgressService.isLessonAllModeFinished(user.id, lessonId, lessonModes)) {
            task.status = 'finished';
            await updateTaskStatusDb(task);
            finishedTasks.push(task);
          }
          break;
        case 'Great Practices':
          if (mode === ModeType.PRACTICE || mode === ModeType.CHALLENGE) {
            if (correctRate && correctRate >= 0.8) {
              let new_task = updateProgress(task, lessonId === 'empty' ? chapterId : lessonId, task.count || 2);
              if (new_task) {
                await updateTaskStatusDb(new_task);
                if (new_task.status === 'finished') {
                  finishedTasks.push(new_task);
                }
              }
            }
          }
          break;
        case 'Daily Chapter':
          if (await isChapterAllFinished(user.id, quest, chapterId, lessonModes)) {
            task.status = 'finished';
            await updateTaskStatusDb(task);
            finishedTasks.push(task);
          }
          break;
        case 'Different Subject Lessons':
          if (await UserQuestProgressService.isLessonAllModeFinished(user.id, lessonId, lessonModes)) {
            let new_task = updateProgress(task, quest.subject!, task.count || 2);
            if (new_task) {
              await updateTaskStatusDb(new_task);
              if (new_task.status === 'finished') {
                finishedTasks.push(new_task);
              }
            }
          }
          break;
      }
    }
  }));

  finishedTasks.forEach(task => {
    addDiamond(task.rewardDiamond);
  });
  return finishedTasks;
};

export const startPractice = async (): Promise<string> => {
  // 生成随机ID (使用时间戳 + 随机数)
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  const practiceId = `practice_${timestamp}_${random}`;

  // 创建答题状态并保存到 localStorage
  const practiceState = {
    id: practiceId,
    created_at: new Date().toISOString(),
    questions: [], // 空列表保存答题状态
    status: 'in_progress'
  };

  // 保存到 localStorage
  localStorage.setItem(`practice_${practiceId}`, JSON.stringify(practiceState));

  return practiceId;
};

export const answerPractice = async (practiceId: string, isCorrect: boolean): Promise<boolean> => {
  // 获取当前答题状态
  const state = localStorage.getItem(`practice_${practiceId}`);
  if (!state) {
    return false;
  }

  // 更新答题记录
  const practiceState = JSON.parse(state);
  practiceState.questions.push({
    isCorrect,
    answered_at: new Date().toISOString()
  });

  // 保存更新后的状态
  localStorage.setItem(`practice_${practiceId}`, JSON.stringify(practiceState));

  // 如果答对了，完成任务TODO
  if (isCorrect) {

  }

  return false;
};

function transformDbTasksToTasks(dbTasks: any[]): Task[] {
  return dbTasks.map(dbTask => (
    {
      ...getTaskByName(dbTask.task_name, dbTask.count || 1, dbTask.quest_id),
      id: dbTask.id,
      progress: dbTask.progress,
      type: dbTask.type,
      status: dbTask.status,
      finished_at: dbTask.finished_at,
      count: dbTask.count,
      quest_id: dbTask.quest_id,
    }
  ));
}

async function getAllTasksStatusFromDB(userId: string): Promise<Task[]> {
  // 获取今天完成的任务
  const localDate = getLocalDateString();

  const { data: dbTasks, error: statusError } = await supabase
    .from('task_status')
    .select('*')
    .eq('user_id', userId)
    .eq('local_date', localDate);

  if (statusError) {
    console.error('Error fetching task status:', statusError);
    throw statusError;
  }
  return transformDbTasksToTasks(dbTasks);
}

async function initializeTasksToDB(tasks: Task[]): Promise<Task[]> {
  const user = useAuthStore.getState().user;
  if (!user) {
    return [];
  }
  const { data, error } = await supabase.from('task_status').insert(tasks.map(task => ({
    user_id: user.id,
    task_name: task.name,
    local_date: getLocalDateString(), // YYYY-MM-DD format in local timezone
    unique_key: task.unique_key || "",
    status: 'initial',
    type: task.type,
    count: task.count,
    quest_id: task.quest_id,
  }))).select();
  if (error || !data || data.length !== tasks.length) {
    console.error('Error saving tasks to DB:', error);
    throw error;
  }
  return transformDbTasksToTasks(data);
}

async function genTodaysTasks(userId: string): Promise<Task[]> {
  const tasks: Task[] = [];
  const scheduleTasks = await getScheduleTasks(userId);
  tasks.push(...scheduleTasks);
  
  // 使用 try-catch 包装 getSubjectMistakesTasks，确保出错不影响后续执行
  // try {
  //   const subjectMistakesTasks = await getSubjectMistakesTasks(userId);
  //   if (subjectMistakesTasks.length > 0) {
  //     tasks.push(...subjectMistakesTasks);
  //   }
  // } catch (error) {
  //   console.error('Error fetching subject mistakes tasks:', error);
  //   // 继续执行，不中断流程
  // }
  
  tasks.push(BASE_TASKS.easy[Math.floor(Math.random() * BASE_TASKS.easy.length)]);
  tasks.push(BASE_TASKS.medium[Math.floor(Math.random() * BASE_TASKS.medium.length)]);
  // tasks.push(BASE_TASKS.hard[Math.floor(Math.random() * BASE_TASKS.hard.length)]);
  return tasks;
}

let isInitializing = false;

export async function getAllTasksStatus(): Promise<Task[]> {
  if (isInitializing) {
    return [];
  }
  try {
    isInitializing = true;
    const user = useAuthStore.getState().user;
    if (!user || !user.id) {
      return [];
    }

    const dbTasks = await getAllTasksStatusFromDB(user.id)
    if (dbTasks.length === 0) {
      console.log("No tasks found, generating today's tasks");
      const tasks = await genTodaysTasks(user.id);
      await initializeTasksToDB(tasks);
      return tasks;
    }
    return dbTasks;
  } finally {
    isInitializing = false;
  }
}

async function getSubjectMistakesTasks(userId: string): Promise<Task[]> {
  const subjectMistakes = await fetchPendingMistakes(userId);
  if (subjectMistakes?.length > 0) {
    const latestMistake = subjectMistakes.sort((a, b) => new Date(b.latestTime).getTime() - new Date(a.latestTime).getTime())[0];
    return [
      {
        name: "mistakes-" + latestMistake.subject,
        description: "Finish mistakes practice in " + latestMistake.subject,
        rewardDiamond: 15,
        type: 'mistakes',
        count: 1,
        unique_key: "mistakes",
      }
    ];
  }
  return [];
}

async function getScheduleTasks(userId: string): Promise<Task[]> {
  try {
    const allQuests = await getAllQuests();
    const allQuestsProgress = await getProgressFromDB(userId);
    const schedules = await getSchedules(allQuests, allQuestsProgress);
    const scheduleTasks = await getScheduleDailyTasks(schedules, allQuests, allQuestsProgress, new Date().toISOString().split('T')[0]);
    return scheduleTasks.map(scheduleTask => (
      {
        name: "schedule-" + scheduleTask.quest_name,
        description: "Complete " + scheduleTask.lessons_per_day + (scheduleTask.lessons_per_day > 1 ? " lessons" : " lesson") + " in " + scheduleTask.quest_name,
        rewardDiamond: 10 * scheduleTask.lessons_per_day,
        type: scheduleTask.type,
        count: scheduleTask.lessons_per_day,
        quest_id: scheduleTask.quest_id,
        unique_key: scheduleTask.quest_id,
      }
    ));
  } catch (error) {
    console.error('Error fetching schedule tasks:', error);
    // 返回空数组，不中断流程
    return [];
  }
}

export const getTodayFinishedLessonCount = async (userId: string): Promise<number> => {
  const now = new Date();
  const localYear = now.getFullYear();
  const localMonth = now.getMonth();
  const localDate = now.getDate();
  const localStart = new Date(localYear, localMonth, localDate, 0, 0, 0); // 本地0点
  const localEnd = new Date(localYear, localMonth, localDate + 1, 0, 0, 0); // 本地次日0点
  const utcStart = localStart.toISOString(); // 本地0点对应的UTC时间
  const utcEnd = localEnd.toISOString();     // 本地次日0点对应的UTC时间
  const finishedLessons = await getFinishedLessonFromDB(userId, utcStart, utcEnd, ["learn", "practice"]);
  return finishedLessons?.length || 0;
}
